import { createInertiaApp } from "@inertiajs/react"
import { createRoot } from "react-dom/client"
import type { ComponentType } from "react"
import { I18nProvider } from "@lingui/react"
import DefaultLayout from "@/layouts/default"
import { i18n, activateLocale } from "@/lib/i18n"
import { installPreloadErrorReload } from "@/lib/reload-on-preload-error"

// Recover navigation when a deploy invalidates the chunk a
// client booted on. Pairs with Inertia's `config.version` 409
// full-reload on the next visit (see config/initializers/inertia.rb).
installPreloadErrorReload()

type PageComponent = ComponentType<Record<string, unknown>>

type PageModule = {
  default: PageComponent
}

// Public/unauthenticated pages must NOT mount DefaultLayout
// (no softphone, no employee-status channel — there's no
// session yet). Matched against the `layout`/`resolve` name
// argument.
const PUBLIC_PAGES = new Set([
  "v2/sessions/new",
  "v2/registrations/new",
  "v2/hello_world",
  // Apex marketing landing (conectocrm.com root) — a guest
  // page, so no DefaultLayout / softphone / channels.
  "v2/landing/index",
])

// Built once at module scope by Vite's static analysis of
// `import.meta.glob` — not rebuilt on every navigation.
//
// The `@` alias resolves to `app/frontend` (see vite.config.mts).
const pages = import.meta.glob<PageModule>(
  ["@/pages/**/*.tsx", "!**/__tests__/**", "!**/*.test.tsx"],
)

createInertiaApp({
  resolve: async (name) => {
    const loader = pages[`../frontend/pages/${name}.tsx`]

    if (!loader) {
      throw new Error(
        `Page not found: ${name}. Expected app/frontend/pages/${name}.tsx`,
      )
    }

    const page = await loader()
    return page.default
  },
  // v3 default-layout option. Returning a bare component
  // reference (NOT wrapped in an arrow) makes DefaultLayout a
  // PERSISTENT layout: it sits ABOVE the keyed page element
  // inside the page-context provider, so its instance survives
  // navigations — keeping the app-global softphone's live
  // connection alive and letting the layout's
  // `usePage`/channel hooks work. v2's `.layout` render-function
  // form is gone; v3 applies `layout` itself. Public pages
  // opt out by returning null (no softphone / channel before
  // a session exists).
  layout: (name) => (PUBLIC_PAGES.has(name) ? null : DefaultLayout),
  setup({ el, App, props }) {
    // Activate the locale the server resolved from
    // Accept-Language, shared as the `locale` Inertia prop
    // (see V2::BaseController). Stable per session, so
    // activating once at boot keeps the React UI in the same
    // language as the server-rendered copy. See
    // docs/translations.md.
    const sharedProps = props.initialPage.props as {
      locale?: string
    }
    activateLocale(sharedProps.locale)

    if (import.meta.env.DEV) {
      // Dev-only: preview a locale in the browser without
      // changing your OS/browser language, e.g.
      // `window.__previewLocale("es-MX")`. Re-renders the app
      // through the live I18nProvider. Stripped from
      // production builds. See docs/translations.md.
      ;(
        window as unknown as {
          __previewLocale?: (locale: string) => void
        }
      ).__previewLocale = activateLocale
    }

    createRoot(el).render(
      <I18nProvider i18n={i18n}>
        <App {...props} />
      </I18nProvider>,
    )
  },
})
